import type { ReactNode } from 'react';
import type { FieldConfig, DisplayFormatterType } from '@/components/blocks/forms';

/**
 * Represents a single item in a DataList for display purposes.
 */
export interface DisplayItem {
  label: string;
  value: ReactNode;
  mono?: boolean;
}

/**
 * Section definition with title and display order.
 */
export interface SectionDefinition {
  title: string;
  order: number;
}

/**
 * A generated section ready for rendering.
 */
export interface GeneratedSection {
  key: string;
  title: string;
  items: DisplayItem[];
}

/**
 * Formatters for different value types.
 */
export interface DisplayFormatters {
  text: (value: unknown) => string;
  date: (value: unknown) => string;
  boolean: (value: unknown) => string;
  number: (value: unknown) => string;
  'month-year': (value: unknown) => string;
}

/**
 * Generates display sections from field configurations and entity data.
 *
 * This utility enables a single source of truth for field labels - define
 * fields once in the form schema with `section` and `displayFormatter`,
 * and this function generates the corresponding display sections.
 *
 * @example
 * ```tsx
 * const sections = generateDisplaySections(
 *   site,
 *   siteFormFields,
 *   {
 *     general: { title: "Informazioni generali", order: 1 },
 *     cpi: { title: "CPI", order: 2 },
 *   },
 *   {
 *     text: formatField,
 *     date: formatIsoDateLabel,
 *     boolean: formatBoolean,
 *     number: (v) => v?.toString() ?? "—",
 *     "month-year": formatMonthYearLabel,
 *   }
 * );
 * ```
 */
export function generateDisplaySections<T extends Record<string, unknown>>(
  data: T | undefined | null,
  fields: readonly FieldConfig[],
  sectionDefs: Record<string, SectionDefinition>,
  formatters: DisplayFormatters
): GeneratedSection[] {
  // Group fields by section, skipping fields without section or with skip formatter
  const sectionGroups = new Map<string, FieldConfig[]>();

  for (const field of fields) {
    if (!field.section) continue;
    if (field.displayFormatter === 'skip') continue;

    const group = sectionGroups.get(field.section) ?? [];
    group.push(field);
    sectionGroups.set(field.section, group);
  }

  // Build sections with formatted values
  const sections: GeneratedSection[] = [];

  for (const [sectionKey, sectionFields] of sectionGroups) {
    const sectionDef = sectionDefs[sectionKey];
    if (!sectionDef) continue;

    const items: DisplayItem[] = [];

    for (const field of sectionFields) {
      const rawValue = data?.[field.key];
      const formatterType: DisplayFormatterType =
        field.displayFormatter ?? inferFormatter(field.type);

      let formattedValue: string;
      if (formatterType === 'select' && field.options) {
        // For select fields, map value to label using options
        const option = field.options.find((opt) => opt.value === rawValue);
        formattedValue = option?.label ?? formatters.text(rawValue);
      } else {
        const formatter =
          formatters[
            formatterType === 'skip' || formatterType === 'select' ? 'text' : formatterType
          ];
        formattedValue = formatter(rawValue);
      }

      items.push({
        label: field.label,
        value: formattedValue,
        mono: field.type === 'tel' || field.type === 'email',
      });
    }

    sections.push({
      key: sectionKey,
      title: sectionDef.title,
      items,
    });
  }

  // Sort by order
  sections.sort((a, b) => {
    const orderA = sectionDefs[a.key]?.order ?? 999;
    const orderB = sectionDefs[b.key]?.order ?? 999;
    return orderA - orderB;
  });

  return sections;
}

/**
 * Infers a display formatter from the field type when not explicitly specified.
 */
function inferFormatter(type: FieldConfig['type']): DisplayFormatterType {
  switch (type) {
    case 'date':
      return 'date';
    case 'month-year':
      return 'month-year';
    case 'checkbox':
      return 'boolean';
    case 'number':
      return 'number';
    default:
      return 'text';
  }
}
